package com.evoapps.evoshift.ui.roster

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.TopBarAction
import com.evoapps.evoshift.widgets.helpAction
import com.evoapps.evoshift.ui.notifications.notificationsAction
import com.evoapps.evoshift.ui.util.HelpCentreButton
import com.evoapps.evoshift.ui.util.HelpPage
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import evoshift.composeapp.generated.resources.roster_skeleton_open
import evoshift.composeapp.generated.resources.roster_skeleton_title
import com.evoapps.evoshift.ui.adaptive.ContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.util.asString
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.Remove
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.viewmodels.CreateRosterViewModel
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.create_roster_draft
import evoshift.composeapp.generated.resources.create_roster_info
import evoshift.composeapp.generated.resources.create_roster_month
import evoshift.composeapp.generated.resources.create_roster_select_period
import evoshift.composeapp.generated.resources.create_roster_title
import evoshift.composeapp.generated.resources.create_roster_year
import org.jetbrains.compose.resources.stringResource
import com.evoapps.evoshift.ui.util.dayInSentence
import kotlinx.datetime.DayOfWeek
import evoshift.composeapp.generated.resources.roster_skeleton_prompt_title
import evoshift.composeapp.generated.resources.roster_skeleton_prompt_no_week
import evoshift.composeapp.generated.resources.roster_skeleton_prompt_missing_days
import evoshift.composeapp.generated.resources.roster_skeleton_prompt_set_up
import evoshift.composeapp.generated.resources.roster_skeleton_prompt_not_now

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreateRosterScreen(
    onNavigateBack: () -> Unit,
    onRosterCreated: (String) -> Unit,
    viewModel: CreateRosterViewModel,
    onOpenWeeklySkeleton: () -> Unit = {}
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()

    // SH-194: back from the Weekly Skeleton (this screen enters the composition again).
    LaunchedEffect(Unit) { viewModel.onReturnedFromSkeletonEditor() }
    LaunchedEffect(state.openSkeletonEditor) {
        if (state.openSkeletonEditor) {
            viewModel.onSkeletonEditorOpened()
            onOpenWeeklySkeleton()
        }
    }

    // SH-194: some days of the week have no day template: offer to set them up now.
    state.skeletonPrompt?.let { missing ->
        val text = if (missing.size == 7) {
            stringResource(Res.string.roster_skeleton_prompt_no_week)
        } else {
            val days = missing.map { dayInSentence(DayOfWeek(it)) }.joinToString(", ")
            stringResource(Res.string.roster_skeleton_prompt_missing_days, days)
        }
        EvoDialog(
            title = stringResource(Res.string.roster_skeleton_prompt_title),
            icon = Icons.Default.Info,
            onDismiss = viewModel::onSkeletonPromptDeclined,
            width = DialogWidth.Compact,
            confirm = DialogAction(stringResource(Res.string.roster_skeleton_prompt_set_up), viewModel::onSkeletonPromptAccepted),
            dismiss = DialogAction(stringResource(Res.string.roster_skeleton_prompt_not_now), viewModel::onSkeletonPromptDeclined)
        ) {
            Text(text, style = MaterialTheme.typography.bodyLarge, color = Color.White.copy(alpha = 0.85f))
        }
    }

    LaunchedEffect(state.successRosterId) {
        state.successRosterId?.let { id ->
            onRosterCreated(id)
        }
    }

    // The month exists but the weekly skeleton put nothing into it: say why before opening it.
    state.skeletonNotice?.let { notice ->
        EvoDialog(
            title = stringResource(Res.string.roster_skeleton_title),
            icon = Icons.Default.Info,
            onDismiss = viewModel::onSkeletonNoticeConfirmed,
            width = DialogWidth.Compact,
            confirm = DialogAction(stringResource(Res.string.roster_skeleton_open), viewModel::onSkeletonNoticeConfirmed),
            dismiss = null
        ) {
            Text(notice.asString(), style = MaterialTheme.typography.bodyLarge, color = Color.White.copy(alpha = 0.85f))
        }
    }

    Scaffold(
        containerColor = Color.Transparent,
        topBar = {
            EvoTopBar(
                title = stringResource(Res.string.create_roster_title),
                onBack = onNavigateBack,
                actions = listOf(helpAction(HelpPage.ROSTER_CREATE))
            )
        }
    ) { padding ->
        com.evoapps.evoshift.widgets.GradientBox(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .contentWidth(ContentMaxWidth)
                    .padding(24.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                RegularCard {
                    Row(Modifier.padding(20.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Info, null, tint = DeepNavy)
                        Spacer(Modifier.width(12.dp))
                        Text(
                            stringResource(Res.string.create_roster_info),
                            style = MaterialTheme.typography.bodySmall,
                            color = DeepNavy.copy(alpha = 0.8f)
                        )
                    }
                }

                Spacer(Modifier.height(32.dp))

                Text(
                    stringResource(Res.string.create_roster_select_period),
                    style = MaterialTheme.typography.titleMedium,
                    color = Color.White
                )

                Spacer(Modifier.height(32.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceEvenly
                ) {
                    NumberSelector(
                        value = state.selectedYear,
                        onValueChange = viewModel::onYearChanged,
                        range = 2025..2030,
                        label = stringResource(Res.string.create_roster_year)
                    )

                    NumberSelector(
                        value = state.selectedMonth,
                        onValueChange = viewModel::onMonthChanged,
                        range = 1..12,
                        label = stringResource(Res.string.create_roster_month)
                    )
                }

                Spacer(Modifier.height(48.dp))

                if (state.isLoading) {
                    CircularProgressIndicator(color = DarkGold)
                } else {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(54.dp)
                            .clip(RoundedCornerShape(12.dp))
                            .background(GoldMetallicBrush)
                            .handCursor().clickable { viewModel.onCreateClicked() },
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            stringResource(Res.string.create_roster_draft),
                            color = DeepNavy,
                            fontWeight = FontWeight.ExtraBold,
                            letterSpacing = 1.sp
                        )
                    }
                }

                if (state.error != null) {
                    Spacer(Modifier.height(16.dp))
                    Text(state.error!!.asString(), color = MaterialTheme.colorScheme.error)
                }
            }
        }
    }
}

@Composable
fun NumberSelector(
    value: Int,
    onValueChange: (Int) -> Unit,
    range: IntRange,
    label: String
) {
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Text(
            label,
            style = MaterialTheme.typography.labelMedium,
            color = Color.White.copy(alpha = 0.6f),
            fontWeight = FontWeight.Bold
        )
        Spacer(Modifier.height(12.dp))
        // Two selectors side by side must fit a 360dp phone: tight spacing, a medium headline.
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .clip(CircleShape)
                    .background(Color.White.copy(alpha = 0.1f))
                    .handCursor().clickable { if (value > range.first) onValueChange(value - 1) },
                contentAlignment = Alignment.Center
            ) {
                Icon(Icons.Default.Remove, null, tint = Color.White)
            }

            Text(
                text = value.toString().padStart(2, '0'),
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
                color = DarkGold
            )

            Box(
                modifier = Modifier
                    .size(40.dp)
                    .clip(CircleShape)
                    .background(Color.White.copy(alpha = 0.1f))
                    .handCursor().clickable { if (value < range.last) onValueChange(value + 1) },
                contentAlignment = Alignment.Center
            ) {
                Icon(Icons.Default.Add, null, tint = Color.White)
            }
        }
    }
}